import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Button, IconButton, MenuItem, TextField, Tooltip } from '@mui/material';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CarrierCodeDto, CarrierCodePlanRow } from '@app/shared';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { FilterPanel } from '../../components/FilterPanel';
import { StatusChip } from '../../components/StatusChip';
import { api } from '../../lib/api';
import { formatAllowance, formatCents, formatDate } from '../../lib/format';
import { useFeatureReleased } from '../../release';
import { useListState } from '../../lib/useListState';
import { CarrierCodeFormDialog } from './CarrierCodeFormDialog';
import { CarrierSetupDialog } from './CarrierSetupDialog';
import { useCarrierCodes } from './api';
import { useCarriers } from './lookups';
import { carrierOptions } from './options';

const yesNoBlank = (v: boolean | null) => (v === null ? '' : v ? 'Yes' : 'No');

const PLAN_ROW_COLUMNS: Column<CarrierCodePlanRow>[] = [
  { key: 'planName', header: 'Plan Name', render: (r) => r.planName },
  { key: 'planType', header: 'Plan Type', render: (r) => r.planType },
  { key: 'tribal', header: 'Tribal', render: (r) => yesNoBlank(r.tribal) },
  { key: 'state', header: 'Plan State', render: (r) => r.state },
  { key: 'status', header: 'Plan Status', render: (r) => r.status },
  { key: 'updatedAt', header: 'Update Date', nowrap: true, render: (r) => formatDate(r.updatedAt) },
  {
    key: 'priceCents',
    header: 'Plan Price',
    align: 'right',
    render: (r) => formatCents(r.priceCents),
  },
];

/** The child rows of a carrier code: its plans, one line per plan and state, read when opened. */
function CodePlans({ code }: { code: CarrierCodeDto }) {
  const plans = useQuery({
    queryKey: ['carrier-codes', code.id, 'plans'],
    queryFn: () =>
      api
        .get<{ data: CarrierCodePlanRow[] }>(`/carrier-codes/${code.id}/plans`)
        .then((r) => r.data),
  });
  return (
    <DataTable
      label={`Plans of ${code.code}`}
      columns={PLAN_ROW_COLUMNS}
      rows={plans.data}
      rowKey={(r) => `${r.planId}:${r.state}`}
      loading={plans.isFetching}
      error={plans.error}
      onRetry={() => void plans.refetch()}
      emptyText="No plan uses this carrier code."
    />
  );
}

/** "Carrier Details" tab of Plan Config: the wholesale plan codes of each carrier. */
export function CarrierDetailsTab() {
  const featureOn = useFeatureReleased();
  const list = useListState({ code: '', carrierId: '' }, 10, 'code');
  const codes = useCarrierCodes(list.params);
  const carriers = useCarriers();
  const [form, setForm] = useState<{ carrierCode?: CarrierCodeDto } | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);

  const columns: Column<CarrierCodeDto>[] = [
    { key: '#', header: '#', render: (_c, i) => rowNumber(i, list.page, list.pageSize), width: 50 },
    {
      key: 'code',
      header: 'Carrier Code',
      sortable: true,
      render: (c) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {c.code}
          {/* The list has no Status column; an inactive code is flagged next to its value. */}
          {c.status === 'Inactive' ? <StatusChip status={c.status} /> : null}
        </Box>
      ),
    },
    { key: 'carrier', header: 'Carrier', render: (c) => c.carrier.code },
    { key: 'type', header: 'Type', sortable: true, render: (c) => c.type },
    { key: 'voiceMinutes', header: 'Voice', render: (c) => formatAllowance(c.voiceMinutes) },
    { key: 'textCount', header: 'Text', render: (c) => formatAllowance(c.textCount) },
    { key: 'dataMb', header: 'Data', render: (c) => formatAllowance(c.dataMb, 'MB') },
    { key: 'billingCode', header: 'Billing Code', sortable: true, render: (c) => c.billingCode },
    { key: 'planCount', header: 'Plans', align: 'right', render: (c) => c.planCount },
    {
      key: 'action',
      header: 'Action',
      render: (c) => (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => setForm({ carrierCode: c })}
            aria-label={`Edit ${c.code}`}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => setSetupOpen(true)}>
          Carrier Setup
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setForm({})}>
          Add new carrier code
        </Button>
      </Box>

      <FilterPanel onSearch={list.search} onReset={list.reset}>
        <TextField
          label="Carrier Code"
          value={list.draft.code}
          onChange={(e) => list.setField('code', e.target.value)}
        />
        <TextField
          select
          label="Carrier"
          value={list.draft.carrierId}
          onChange={(e) => list.setField('carrierId', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {carrierOptions(carriers.data).map((c) => (
            <MenuItem key={c.value} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterPanel>

      <DataTable
        label="Carrier codes"
        columns={columns}
        rows={codes.data?.data}
        rowKey={(c) => c.id}
        loading={codes.isFetching}
        error={codes.error}
        onRetry={() => void codes.refetch()}
        page={list.page}
        pageSize={list.pageSize}
        total={codes.data?.total ?? 0}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        sort={list.sort}
        onSortChange={list.setSort}
        rowDetail={
          featureOn('carrier-code-plans')
            ? { render: (c) => <CodePlans code={c} />, label: (c) => `plans of ${c.code}` }
            : undefined
        }
      />

      <CarrierCodeFormDialog
        open={Boolean(form)}
        carrierCode={form?.carrierCode}
        onClose={() => setForm(null)}
      />
      <CarrierSetupDialog open={setupOpen} onClose={() => setSetupOpen(false)} />
    </Box>
  );
}
